import React from "react";
import { Download, File, Database, Folder, Badge } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { toast } from "sonner";
import { stripTimestamp } from "@/utils/projectManagement.utils";

export interface DatasetFile {
  presignedUrl: string;
  key: string;
  fileName: string;
  fileSize: number;
  contentType?: string;
  lastModified?: string;
  etag?: string;
  metadata?: any;
  source?: "legacy" | "repo";
  datasetTitle?: string;
}

interface ProjectFilesSectionProps {
  files?: DatasetFile[];
  datasets?: Array<{
    id: string;
    title: string;
    s3_keys?: any[];
    s3Keys?: any[];
  }>;
}

export const ProjectFilesSection = ({
  files = [],
  datasets = [],
}: ProjectFilesSectionProps) => {
  // Flatten repo datasets to DatasetFile using actual API structure
  // Supports both s3_keys (snake_case) and s3Keys (camelCase) for cross-project compatibility
  const repoFiles: DatasetFile[] = datasets.flatMap((dataset) => {
    const keys = dataset.s3_keys || dataset.s3Keys || [];
    return keys.map((s3key: any) => ({
      presignedUrl: s3key.download_url?.presignedUrl || s3key.s3_url || "",
      key: s3key.key,
      fileName: s3key.originalName || s3key.key.split("/").pop() || "unknown",
      fileSize: s3key.size || 0,
      contentType: s3key.type,
      source: "repo" as const,
      datasetTitle: dataset.title,
      metadata: { datasetId: dataset.id, datasetTitle: dataset.title },
    }));
  });

  // Combine repo + legacy
  const allFiles: DatasetFile[] = [...repoFiles, ...files];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDownload = async (file: DatasetFile) => {
    if (!file.presignedUrl) {
      toast.error("No download URL available");
      return;
    }
    const cleanFileName = stripTimestamp(file.fileName);
    try {
      const link = document.createElement("a");
      link.href = file.presignedUrl;
      link.download = cleanFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Downloaded ${cleanFileName}`);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border">
      {/* Header */}
      <div className="p-5 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold text-gray-800">Project Files</h2>
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-500">
          {files.length > 0 && (
            <span className="flex items-center gap-1">
              <Folder className="h-4 w-4 text-green-500" />
              {files.length}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4 max-h-[500px] overflow-y-auto">
        {allFiles.length > 0 ? (
          allFiles.map((file, index) => {
            const cleanFileName = stripTimestamp(file.fileName);
            return (
              <div
                key={`${file.key}-${index}`}
                className="group border rounded-xl p-4 hover:shadow-md hover:border-blue-200 transition-all bg-white"
              >
                {/* Top Row */}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate" title={cleanFileName}>
                      {cleanFileName}
                    </h3>

                    {file.datasetTitle && (
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        From: {file.datasetTitle}
                      </p>
                    )}

                    {file.lastModified && (
                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(file.lastModified)}
                      </p>
                    )}
                  </div>

                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(file)}
                    disabled={!file.presignedUrl}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>

                {/* Bottom Row */}
                <div className="flex items-center justify-between mt-4 text-xs text-gray-600">
                  <span>{formatFileSize(file.fileSize)}</span>

                  <div className="flex items-center gap-2">
                    {file.datasetTitle && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                        Dataset
                      </span>
                    )}

                    {file.source === "repo" && (
                      <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        Repo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-16">
            <File className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No datasets available</p>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {allFiles.length > 0 && (
        <div className="border-t px-5 py-3 bg-gray-50 flex justify-between text-sm text-gray-600 rounded-b-2xl">
          <span>Total: {allFiles.length}</span>
          <span>Repo: {repoFiles.length}</span>
        </div>
      )}
    </div>
  );
};
